import type { Stage } from "@/types/journey";

interface Props {
  progress: 0 | 1 | 2 | 3 | 4;
  stage: Stage;
}

const sceneLabels = ["岸边 / 清晨", "离岸 / 首创", "湖心 / 奋斗", "穿雾 / 奉献", "开阔水面 / 出发"];
const sceneNotes = [
  "薄雾还没有散去，船在等一个真实的问题。",
  "第一道水纹从岸边打开，选择开始有了方向。",
  "风从湖心吹来，答案不再只停留在想象里。",
  "船穿过雾线，留下的行动开始变得明亮。",
  "水面打开，下一步由你自己命名。",
];

function sceneProgress(stage: Stage): 0 | 1 | 2 | 3 | 4 {
  if (stage === "QUESTION_ONE") return 1;
  if (stage === "QUESTION_TWO") return 2;
  if (stage === "QUESTION_THREE" || stage === "JOURNEY_RECAP" || stage === "PERSONAL_REFLECTION" || stage === "KNOWLEDGE" || stage === "JIAXING_CASE" || stage === "ACTION_INPUT" || stage === "ACTION_REFINE") return 3;
  if (stage === "TICKET" || stage === "LIGHTS" || stage === "END") return 4;
  return 0;
}

export function BoatScene({ progress, stage }: Props) {
  const visualProgress = sceneProgress(stage);
  const boatX = [0, 104, 208, 306, 405][visualProgress];
  const boatY = [13, 9, 4, -3, -8][visualProgress];
  const mistOpacity = [1, 0.86, 0.62, 0.34, 0.1][visualProgress];
  const glowOpacity = [0.18, 0.25, 0.34, 0.5, 0.7][visualProgress];
  const windOpacity = visualProgress >= 2 ? 0.65 : 0;
  const label = sceneLabels[visualProgress];
  const note = sceneNotes[visualProgress];

  return (
    <div className={`boat-scene boat-progress-${progress} scene-stage-${visualProgress}`} aria-hidden="true">
      <div className="scene-meta">
        <span>{label}</span>
        <span className="scene-meter"><i style={{ width: `${Math.max(9, visualProgress * 25)}%` }} /></span>
      </div>
      <svg viewBox="0 0 960 420" role="presentation" focusable="false">
        <defs>
          <linearGradient id="sceneSky" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#c9dcd7" />
            <stop offset="0.58" stopColor="#dfe9e2" />
            <stop offset="1" stopColor="#eef0e9" />
          </linearGradient>
          <linearGradient id="sceneWater" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#a8c5c4" />
            <stop offset="0.45" stopColor="#83aeb0" />
            <stop offset="1" stopColor="#668f96" />
          </linearGradient>
          <linearGradient id="sceneMist" x1="0" x2="1">
            <stop offset="0" stopColor="#f5f1e8" stopOpacity=".96" />
            <stop offset="0.54" stopColor="#e6eee7" stopOpacity=".72" />
            <stop offset="1" stopColor="#dce9e1" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="sceneSun" cx="50%" cy="50%" r="50%">
            <stop offset="0" stopColor="#fff2c7" stopOpacity=".78" />
            <stop offset="0.44" stopColor="#f6e6bf" stopOpacity=".28" />
            <stop offset="1" stopColor="#f6e6bf" stopOpacity="0" />
          </radialGradient>
          <filter id="boatShadow" x="-25%" y="-30%" width="150%" height="180%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="6" />
            <feOffset dy="8" />
            <feComponentTransfer><feFuncA type="linear" slope=".2" /></feComponentTransfer>
            <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <filter id="reflectionBlur" x="-30%" y="-50%" width="160%" height="220%">
            <feGaussianBlur stdDeviation="4" />
          </filter>
        </defs>

        <rect width="960" height="420" fill="url(#sceneSky)" />
        <circle cx="790" cy="102" r="125" fill="url(#sceneSun)" opacity={glowOpacity} />
        <path className="scene-hill scene-hill-back" d="M0 177C105 144 180 174 284 155c111-20 208-10 305 16 119 32 232 12 371-22v92H0z" />
        <path className="scene-hill scene-hill-mid" d="M0 209c100-28 180-2 276-18 110-18 194-23 301 3 131 31 242 12 383-27v99H0z" />
        <path className="scene-hill scene-hill-front" d="M0 249c135-32 214-1 320-13 120-14 230-33 347-4 116 28 201 5 293-24v77H0z" />
        <rect y="248" width="960" height="172" fill="url(#sceneWater)" />

        <g className="scene-mist scene-mist-back mist-ribbons" opacity={mistOpacity * 0.82}>
          <path d="M-50 160C125 108 265 174 421 143c173-34 321-16 596-61v85c-190 41-368 19-523 48-192 36-313-28-544 34z" fill="url(#sceneMist)" />
          <path d="M-70 220c184-57 305-7 460-23 178-18 333-72 618-45v67c-213-9-394 52-580 51-189-1-298-34-498 26z" fill="#eef1e9" opacity=".66" />
        </g>
        <g className="scene-mist scene-mist-front" opacity={mistOpacity * 0.58}>
          <path d="M-40 270c170-37 307 2 443-8 178-13 343-58 602-30v51c-209-5-369 37-566 39-200 2-329-30-519 20z" fill="#f3f1e9" opacity=".54" />
        </g>

        <g className="scene-water-lines scene-water-far" opacity=".48">
          <path d="M26 276c108-14 177 7 284-4 120-12 184 8 298-2 118-10 207 6 328-18" />
          <path d="M-12 294c112-11 198 4 303-4 123-10 213 9 323-3 115-12 216 2 349-16" />
          <path d="M74 317c91-10 164 5 256-2 116-9 179 9 292-2 128-12 196 4 303-9" />
        </g>
        <g className="scene-water-lines scene-water-near" opacity=".78">
          <path d="M-24 342c148-17 231 8 363-4 142-13 229 8 353-4 112-11 178 0 294-17" />
          <path d="M28 367c123-13 211 5 337-5 141-11 230 11 353-2 108-12 171 1 274-12" />
          <path d="M-36 396c151-14 267 3 397-7 135-10 233 8 369-3 94-8 154 3 267-9" />
          <path d="M210 410c82-7 152 3 229-3 102-8 176 6 276-2" />
        </g>
        <g className="scene-wind-lines" opacity={windOpacity}>
          <path d="M36 260c84-22 143-18 221-4" />
          <path d="M685 289c74-19 145-17 223-4" />
          <path d="M35 329c56-14 107-15 160-8" />
        </g>

        <g className="boat-ripples" opacity={0.36 + visualProgress * 0.08}>
          <ellipse cx={265 + boatX} cy={309 + boatY} rx="126" ry="11" />
          <ellipse cx={265 + boatX} cy={310 + boatY} rx="184" ry="20" />
          <ellipse cx={265 + boatX} cy={312 + boatY} rx="238" ry="31" />
        </g>
        <g className="boat-reflection" transform={`translate(${boatX} ${boatY + 370}) scale(1 -0.24)`} filter="url(#reflectionBlur)" opacity={0.3 - visualProgress * 0.035}>
          <path className="boat-hull" d="M115 265c57 29 184 35 279-7l-20 47c-105 26-210 20-246-10z" />
          <path className="boat-cabin" d="M168 247h165l-15 29H183z" />
          <path className="boat-sail" d="M255 111l81 70h-81z" />
        </g>

        <g className="boat-group" style={{ transform: `translate(${boatX}px, ${boatY}px)` }} filter="url(#boatShadow)">
          <path className="boat-shadow" d="M104 305c77 17 220 20 322-10 22-6 46-5 63 3-104 44-284 54-385 24z" />
          <path className="boat-hull" d="M115 265c57 29 184 35 279-7l-20 47c-105 26-210 20-246-10z" />
          <path className="boat-hull-highlight" d="M130 270c72 22 166 25 249-4-69 35-172 46-247 21z" />
          <path className="boat-keel" d="M127 294c84 22 177 19 250-7l-15 18c-92 27-183 22-228 8z" />
          <path className="boat-cabin" d="M168 247h165l-15 29H183z" />
          <path className="boat-roof" d="M158 247c41-14 119-15 179 0l-12 8H169z" />
          <path className="boat-window" d="M187 253h25v15h-25zm37 0h25v15h-25zm37 0h25v15h-25z" />
          <path className="boat-post" d="M255 250V103l81 78" />
          <path className="boat-sail" d="M260 112l76 69h-76z" />
          <path className="boat-sail-shadow" d="M260 112l76 69h-12l-64-32z" />
          <path className="boat-flag" d="M254 103l28 8-28 10z" />
          <path className="boat-rope" d="M256 104L191 244M335 181L345 245" />
          <path className="boat-deck" d="M141 276c45 13 116 15 167 2l43-13c-64 32-154 37-222 17z" />
        </g>
      </svg>
      <div className="scene-caption"><strong>南湖航迹 / 0{visualProgress}</strong><span>{note}</span></div>
    </div>
  );
}
